import type { PhonemeGuideEntry } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/pianoRoll/phonemes/types";

// The 39 CMU/ARPAbet phonemes the dictionary speaks, the model's own reduced and fused sounds (ax, dx, q, dr, tr), plus the backend's two specials, each with an everyday word that carries the sound. The picker and the hover hint read this so a producer never has to learn the alphabet: "uw" means nothing, "uw — as in too" does. Insertion order is the picker's order — vowels front-to-back-ish by mouth position, consonants alphabetically — so the groups read the same every time. Examples are chosen so the sound sits in a stressed, obvious place in a short word.
export const PHONEME_GUIDE: Record<string, PhonemeGuideEntry> = {
  iy: { kind: "vowel", example: "see" },
  ih: { kind: "vowel", example: "sit" },
  ey: { kind: "vowel", example: "day" },
  eh: { kind: "vowel", example: "bed" },
  ae: { kind: "vowel", example: "cat" },
  aa: { kind: "vowel", example: "father" },
  ah: { kind: "vowel", example: "cup" },
  ax: { kind: "vowel", example: "about" },
  ao: { kind: "vowel", example: "law" },
  ow: { kind: "vowel", example: "go" },
  uh: { kind: "vowel", example: "book" },
  uw: { kind: "vowel", example: "too" },
  er: { kind: "vowel", example: "her" },
  ay: { kind: "vowel", example: "my" },
  aw: { kind: "vowel", example: "now" },
  oy: { kind: "vowel", example: "boy" },
  b: { kind: "consonant", example: "bee" },
  ch: { kind: "consonant", example: "chin" },
  d: { kind: "consonant", example: "day" },
  dh: { kind: "consonant", example: "this" },
  dr: { kind: "consonant", example: "dry" },
  dx: { kind: "consonant", example: "butter" },
  f: { kind: "consonant", example: "fun" },
  g: { kind: "consonant", example: "go" },
  hh: { kind: "consonant", example: "hat" },
  jh: { kind: "consonant", example: "joy" },
  k: { kind: "consonant", example: "key" },
  l: { kind: "consonant", example: "love" },
  m: { kind: "consonant", example: "me" },
  n: { kind: "consonant", example: "no" },
  ng: { kind: "consonant", example: "sing" },
  p: { kind: "consonant", example: "pay" },
  q: { kind: "consonant", example: "uh-oh" },
  r: { kind: "consonant", example: "red" },
  s: { kind: "consonant", example: "so" },
  sh: { kind: "consonant", example: "she" },
  t: { kind: "consonant", example: "tea" },
  th: { kind: "consonant", example: "thin" },
  tr: { kind: "consonant", example: "try" },
  v: { kind: "consonant", example: "voice" },
  w: { kind: "consonant", example: "we" },
  y: { kind: "consonant", example: "yes" },
  z: { kind: "consonant", example: "zoo" },
  zh: { kind: "consonant", example: "vision" },
  // The two backend symbols are not sounds; their "example" is what they do, and formatPhonemeHoverHint prints it as such.
  sp: { kind: "special", example: "silence" },
  ap: { kind: "special", example: "breath" },
};

// For each phoneme, the sounds a fix is most likely one step away from, closest first: vowels by neighbouring mouth position, consonants by voiced/unvoiced pairs and shared place of articulation. The picker shows these at the top so "the vowel is a bit too closed" is one click, not a scan of fifteen options.
export const PHONEME_NEIGHBOURS: Record<string, readonly string[]> = {
  iy: ["ih", "ey"],
  ih: ["iy", "eh", "ey"],
  ey: ["ih", "eh", "iy"],
  eh: ["ae", "ih", "ey"],
  ae: ["eh", "ah", "aa"],
  aa: ["ao", "ah", "ae"],
  ah: ["aa", "uh", "er"],
  ax: ["ah", "ih", "uh"],
  ao: ["aa", "ow", "uh"],
  ow: ["ao", "uw", "uh"],
  uh: ["uw", "ow", "ah"],
  uw: ["uh", "ow", "ao"],
  er: ["ah", "uh", "eh"],
  ay: ["aa", "ey", "eh"],
  aw: ["aa", "ow", "ah"],
  oy: ["ao", "ay", "ow"],
  b: ["p", "v", "m"],
  p: ["b", "f"],
  d: ["t", "dh", "g"],
  t: ["d", "th", "k"],
  dx: ["t", "d"],
  q: ["t", "hh"],
  dr: ["d", "jh", "tr"],
  tr: ["t", "ch", "dr"],
  g: ["k", "d"],
  k: ["g", "t"],
  f: ["v", "th", "p"],
  v: ["f", "dh", "b"],
  th: ["dh", "f", "t"],
  dh: ["th", "v", "d"],
  s: ["z", "sh", "th"],
  z: ["s", "zh", "dh"],
  sh: ["s", "zh", "ch"],
  zh: ["sh", "z", "jh"],
  ch: ["jh", "sh", "t"],
  jh: ["ch", "zh", "d"],
  m: ["n", "b"],
  n: ["m", "ng", "d"],
  ng: ["n", "g"],
  l: ["r", "w"],
  r: ["l", "er", "w"],
  w: ["uw", "r", "l"],
  y: ["iy", "ih"],
  hh: ["f", "th"],
  sp: ["ap"],
  ap: ["sp"],
};

// Inventory tokens that are model markers rather than sounds a producer would pick: the hover hint names them neutrally instead of calling them unknown, and the picker never offers them. What vf marks is not confirmed yet (ask the backend).
export const PHONEME_MODEL_MARKERS: Record<string, string> = {
  cl: "the closure before a stop consonant",
  vf: "a model marker",
};
